'use client';

/**
 * ReauthModal Component
 *
 * Modal dialog for in-place re-authentication without leaving the call.
 * Supports both credential-based login and SSO redirect flows.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { LogIn, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { createClient } from '@/lib/supabase/client';
import { useAuthHealthContextSafe } from '@/contexts/AuthHealthContext';
import logger from '@/lib/logger';

// ============================================================================
// TYPES
// ============================================================================

export interface ReauthModalProps {
  /**
   * Whether the modal is open
   */
  isOpen: boolean;

  /**
   * Callback when modal is closed
   */
  onClose: () => void;

  /**
   * Callback when re-auth succeeds
   */
  onSuccess?: () => void;

  /**
   * Whether to show SSO option (default: true)
   */
  showSsoOption?: boolean;
}

type ReauthStatus = 'idle' | 'loading' | 'success' | 'error';

/** Auto-refresh interval when modal is open (30 seconds) */
const AUTO_REFRESH_INTERVAL_MS = 30000;

/** Number of failed refreshes before showing the modal */
const REFRESH_FAILURE_THRESHOLD = 3;

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Modal for in-place re-authentication during active calls.
 *
 * @example
 * ```tsx
 * <ReauthModal
 *   isOpen={showReauthModal}
 *   onClose={() => setShowReauthModal(false)}
 *   onSuccess={() => {
 *     setShowReauthModal(false);
 *     authHealth.clearAuthError();
 *   }}
 * />
 * ```
 */
export function ReauthModal({
  isOpen,
  onClose,
  onSuccess,
  showSsoOption = true,
}: ReauthModalProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<ReauthStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const authHealth = useAuthHealthContextSafe();
  const autoRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const autoRefreshAttemptsRef = useRef(0);

  // Auto-refresh: attempt token refresh every 30s while modal is open
  useEffect(() => {
    if (!isOpen) {
      // Reset on close
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
        autoRefreshIntervalRef.current = null;
      }
      autoRefreshAttemptsRef.current = 0;
      return;
    }

    const attemptAutoRefresh = async () => {
      if (status === 'loading' || status === 'success') return;

      autoRefreshAttemptsRef.current++;
      logger.debug('ReauthModal', 'Auto-refresh attempt', { attempt: autoRefreshAttemptsRef.current });

      try {
        const supabase = createClient();
        const { data, error: refreshError } = await supabase.auth.refreshSession();

        if (!refreshError && data.session) {
          logger.info('ReauthModal', 'Auto-refresh succeeded');
          setStatus('success');
          if (authHealth) {
            authHealth.clearAuthError();
          }
          setTimeout(() => {
            onSuccess?.();
            onClose();
          }, 1000);
        }
      } catch {
        // Silent failure - will retry on next interval
      }
    };

    // Start auto-refresh interval
    autoRefreshIntervalRef.current = setInterval(attemptAutoRefresh, AUTO_REFRESH_INTERVAL_MS);

    return () => {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
        autoRefreshIntervalRef.current = null;
      }
    };
  }, [isOpen, status, authHealth, onSuccess, onClose]);

  const handleCredentialLogin = useCallback(async () => {
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    setStatus('loading');
    setError(null);

    try {
      const supabase = createClient();

      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) {
        logger.warn('ReauthModal', 'Credential login failed', { error: authError.message });
        setError(authError.message);
        setStatus('error');
        return;
      }

      if (data.session) {
        logger.info('ReauthModal', 'Re-authentication successful');
        setStatus('success');

        // Clear auth error state
        if (authHealth) {
          authHealth.clearAuthError();
        }

        // Call success callback after a brief delay to show success state
        setTimeout(() => {
          onSuccess?.();
          onClose();
        }, 1000);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Authentication failed';
      logger.error('ReauthModal', 'Re-authentication error', { error: message });
      setError(message);
      setStatus('error');
    }
  }, [email, password, authHealth, onSuccess, onClose]);

  const handleSessionRefresh = useCallback(async () => {
    setStatus('loading');
    setError(null);

    try {
      const supabase = createClient();
      const { data, error: refreshError } = await supabase.auth.refreshSession();

      if (refreshError) {
        logger.warn('ReauthModal', 'Session refresh failed', { error: refreshError.message });
        setError(refreshError.message);
        setStatus('error');
        return;
      }

      if (data.session) {
        logger.info('ReauthModal', 'Session refresh successful');
        setStatus('success');

        if (authHealth) {
          authHealth.clearAuthError();
        }

        setTimeout(() => {
          onSuccess?.();
          onClose();
        }, 1000);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Session refresh failed';
      logger.error('ReauthModal', 'Session refresh error', { error: message });
      setError(message);
      setStatus('error');
    }
  }, [authHealth, onSuccess, onClose]);

  const handleSsoLogin = useCallback(async () => {
    setStatus('loading');
    setError(null);

    try {
      const supabase = createClient();
      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.href,
        },
      });

      if (oauthError) {
        logger.warn('ReauthModal', 'Google SSO failed', { error: oauthError.message });
        setError(oauthError.message);
        setStatus('error');
      }
      // If no error, browser will redirect to Google OAuth flow
    } catch (err) {
      const message = err instanceof Error ? err.message : 'SSO login failed';
      logger.error('ReauthModal', 'SSO login error', { error: message });
      setError(message);
      setStatus('error');
    }
  }, []);

  const handleClose = useCallback(() => {
    // Reset state on close
    setEmail('');
    setPassword('');
    setStatus('idle');
    setError(null);
    onClose();
  }, [onClose]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent layout="sections" size="sm" data-testid="reauth-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LogIn className="icon" aria-hidden="true" />
            Re-authenticate
          </DialogTitle>
          <DialogDescription data-testid="reauth-message">
            Your session has expired. Please sign in again to continue using all features.
            You can stay in the call while re-authenticating.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
        <div className="space-y-4 py-2">
          {/* Success message */}
          {status === 'success' && (
            <div className="flex items-center gap-3 rounded-item border border-success-edge bg-success-soft p-4">
              <CheckCircle className="icon text-success" aria-hidden="true" />
              <p className="text-body text-success-on-soft">Successfully re-authenticated!</p>
            </div>
          )}

          {/* Error message */}
          {error && status === 'error' && (
            <div className="flex items-center gap-3 rounded-item border border-danger-edge bg-danger-soft p-4">
              <AlertCircle className="icon text-danger" aria-hidden="true" />
              <p className="text-body text-danger-on-soft">{error}</p>
            </div>
          )}

          {/* Try session refresh first */}
          {status !== 'success' && (
            <div className="space-y-3">
              <Button
                data-testid="reauth-btn-refresh"
                variant="outline"
                onClick={handleSessionRefresh}
                disabled={status === 'loading'}
                className="w-full"
              >
                {status === 'loading' ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Refreshing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try Refreshing Session
                  </>
                )}
              </Button>

              {/* Google SSO button */}
              {showSsoOption && (
                <Button
                  data-testid="reauth-btn-sso"
                  variant="outline"
                  onClick={handleSsoLogin}
                  disabled={status === 'loading'}
                  className="w-full"
                >
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  Sign in with Google
                </Button>
              )}

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-edge" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-panel px-2 text-ink-muted">
                    Or sign in with credentials
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Credential form */}
          {status !== 'success' && (
            <div className="space-y-3">
              <div>
                <Label htmlFor="email" className="mb-1">
                  Email
                </Label>
                <Input
                  data-testid="reauth-input-email"
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  disabled={status === 'loading'}
                />
              </div>
              <div>
                <Label htmlFor="password" className="mb-1">
                  Password
                </Label>
                <Input
                  data-testid="reauth-input-password"
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  disabled={status === 'loading'}
                  onKeyDown={(e) => e.key === 'Enter' && handleCredentialLogin()}
                />
              </div>
            </div>
          )}

          {/* SSO option - removed, Google SSO button is now above the form */}
        </div>
        </DialogBody>

        <DialogFooter>
          <Button
            data-testid="reauth-btn-cancel"
            variant="outline"
            onClick={handleClose}
            disabled={status === 'loading'}
          >
            Cancel
          </Button>
          {status !== 'success' && (
            <Button
              data-testid="reauth-btn-login"
              onClick={handleCredentialLogin}
              disabled={status === 'loading' || !email || !password}
            >
              {status === 'loading' ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <LogIn className="w-4 h-4 mr-2" />
                  Sign In
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ReauthModal;
